/* Feb-2026 · AR / AP Aging Report
 *
 * Buckets pending Accounts Receivable and Accounts Payable ledger rows
 * by age: 0-30, 31-60, 61-90, 90+. Click a bucket to expand items.
 * Backend: GET /api/admin/gl/aging
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PanelHeader } from './_shared';
import { RefreshCw, Loader2, ArrowDownRight, ArrowUpRight, ChevronRight, ChevronDown, Mail } from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import { LedgerAccessLink } from './LedgerAccessLink';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const BUCKET_ORDER = ['0_30', '31_60', '61_90', 'over_90'];
const BUCKET_LABEL = { '0_30': '0–30 days', '31_60': '31–60 days', '61_90': '61–90 days', over_90: '90+ days' };
const BUCKET_TONE = {
  '0_30':   'border-emerald-200 bg-emerald-50 text-emerald-900',
  '31_60':  'border-amber-200 bg-amber-50 text-amber-900',
  '61_90':  'border-orange-200 bg-orange-50 text-orange-900',
  over_90:  'border-rose-300 bg-rose-50 text-rose-900',
};
const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function BucketCard({ bucketKey, bucket, open, onToggle, direction, onChase, chasingId }) {
  const tone = BUCKET_TONE[bucketKey];
  return (
    <div className={`rounded-md border ${tone}`} data-testid={`aging-${direction}-${bucketKey}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
        data-testid={`aging-${direction}-${bucketKey}-toggle`}
      >
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide">{BUCKET_LABEL[bucketKey]}</div>
          <div className="text-sm font-medium">{fmtUsd(bucket.total_usd)} <span className="text-[11px] opacity-75">· {bucket.count} row{bucket.count === 1 ? '' : 's'}</span></div>
        </div>
        {bucket.count > 0 && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
      </button>
      {open && bucket.items.length > 0 && (
        <div className="border-t border-current/20 bg-white/60">
          <table className="w-full text-[11.5px]">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 text-left">Party</th>
                <th className="px-3 py-1.5 text-left">Category</th>
                <th className="px-3 py-1.5 text-right">Amount</th>
                <th className="px-3 py-1.5 text-right">Days</th>
                {direction === 'ar' && <th className="px-3 py-1.5 text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {bucket.items.slice(0, 30).map((it) => (
                <tr key={it.id} className="border-t border-current/10">
                  <td className="px-3 py-1.5">{it.party}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{it.category}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{fmtUsd(it.amount_usd)}</td>
                  <td className="px-3 py-1.5 text-right">{it.days_out}</td>
                  {direction === 'ar' && (
                    <td className="px-3 py-1.5 text-right">
                      <Button size="sm" variant="outline" disabled={chasingId === it.id} onClick={() => onChase(it)} data-testid={`aging-chase-${it.id}`}>
                        {chasingId === it.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Mail className="mr-1 h-3 w-3" />}
                        Remind
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AgingReportPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [chasingId, setChasingId] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/gl/aging');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aging report failed to load.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggle = (dir, bucket) => setOpen((s) => ({ ...s, [`${dir}:${bucket}`]: !s[`${dir}:${bucket}`] }));

  const chaseOne = async (item) => {
    const ok = await openConfirm({ title: 'Send payment reminder?', description: `Send the configured A/R reminder for ${item.party}?`, confirmLabel: 'Send reminder' });
    if (!ok) return;
    setChasingId(item.id);
    try {
      const response = await http.post(`/admin/gaap/ar/chase/${encodeURIComponent(item.id)}`);
      toast.success(response.data.message || 'Reminder sent.');
      await reload();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Reminder failed.'); }
    finally { setChasingId(null); }
  };

  const chaseBulk = async () => {
    const ok = await openConfirm({ title: 'Send all overdue reminders?', description: 'This sends one reminder per customer with an outstanding A/R balance.', confirmLabel: 'Send reminders' });
    if (!ok) return;
    setChasingId('bulk');
    try {
      const response = await http.post('/admin/gaap/ar/chase-bulk', { buckets: ['1_30', '31_60', '61_90', 'over_90'] });
      toast.success(`Dunning complete · ${response.data.sent || 0} sent.`);
      await reload();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Bulk reminders failed.'); }
    finally { setChasingId(null); }
  };

  return (
    <div className="space-y-4" data-testid="aging-report-panel">
      <PanelHeader
        icon={ArrowDownRight}
        title="AR / AP Aging"
        subtitle="Outstanding receivables and payables bucketed by age. Chase red buckets first."
      />
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={reload} disabled={busy} data-testid="aging-refresh">
          {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
          Refresh
        </Button>
        <Button size="sm" onClick={chaseBulk} disabled={chasingId === 'bulk' || !data?.accounts_receivable?.total_count} data-testid="aging-chase-bulk">
          {chasingId === 'bulk' ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-2 h-3.5 w-3.5" />}
          Send overdue reminders
        </Button>
        <LedgerAccessLink testId="aging-open-ledger" />
        {data?.as_of_date && (
          <span className="ml-auto text-[11px] text-muted-foreground">As of {data.as_of_date}</span>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
              <div className="text-[12px] font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
                <ArrowDownRight className="h-3.5 w-3.5 text-emerald-600" /> Accounts Receivable
              </div>
              <div className="text-sm font-mono-stat" data-testid="aging-ar-total">
                {fmtUsd(data.accounts_receivable.total_usd)} · {data.accounts_receivable.total_count} row(s)
              </div>
            </div>
            {BUCKET_ORDER.map((b) => (
              <BucketCard
                key={`ar-${b}`}
                bucketKey={b}
                direction="ar"
                bucket={data.accounts_receivable.buckets[b]}
                open={!!open[`ar:${b}`]}
                onToggle={() => toggle('ar', b)}
                onChase={chaseOne}
                chasingId={chasingId}
              />
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
              <div className="text-[12px] font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
                <ArrowUpRight className="h-3.5 w-3.5 text-rose-600" /> Accounts Payable
              </div>
              <div className="text-sm font-mono-stat" data-testid="aging-ap-total">
                {fmtUsd(data.accounts_payable.total_usd)} · {data.accounts_payable.total_count} row(s)
              </div>
            </div>
            {BUCKET_ORDER.map((b) => (
              <BucketCard
                key={`ap-${b}`}
                bucketKey={b}
                direction="ap"
                bucket={data.accounts_payable.buckets[b]}
                open={!!open[`ap:${b}`]}
                onToggle={() => toggle('ap', b)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
